import { Component, OnInit, Input, Output, EventEmitter, OnChanges } from '@angular/core';
import { MenuItem, MeteringPoint, ListStyle } from '@model';
import { Router } from '@angular/router';
import { animate, group, state, style, transition, trigger } from '@angular/animations';
import { SxNavigationMenuService, SxPermissionService } from '@services';
import { MULTI_LEVEL_MENU_CONSTANT } from '@constants';

@Component({
    selector: 'app-multi-level-menu-item',
    templateUrl: './multi-level-menu-item.component.html',
    animations: [
        trigger('slideInOut', [
            state('in', style({ height: '*', opacity: 0 })),
            transition(':leave', [
                style({ height: '*', opacity: 0.2 }),
                group([animate(200, style({ height: 0 })), animate('200ms ease-out', style({ opacity: 0 }))])
            ]),
            transition(':enter', [
                style({ height: '0', opacity: 0 }),
                group([animate(200, style({ height: '*' })), animate('400ms ease-out', style({ opacity: 1 }))])
            ])
        ]),
        trigger('isExpandedLTR', [
            state('yes', style({ transform: 'rotate(-180deg)' })),
            state('no', style({ transform: 'rotate(0deg)' })),
            transition('no => yes', animate(200)),
            transition('yes => no', animate(200))
        ])
    ],
    standalone: false
})
export class MultiLevelMenuItemComponent implements OnInit, OnChanges {
  @Input() node: MenuItem;
  @Input() level = 1;
  @Input() submenuLevel = 0;
  @Input() selectedNode: MenuItem;
  @Input() meteringPoints: { meteringPoints: MeteringPoint[]; loaded: boolean };
  @Input() isWhiteLabel: boolean;

  @Output() selectedItem = new EventEmitter<MenuItem>();

  children: MenuItem[];
  classes: { [index: string]: boolean };
  firstInitializer = false;
  appRouter: Router;
  collapseOnSelect = true;
  onlyExternalMeteringPoints = false

  constructor(
    private router: Router,
    public navigationMenuService: SxNavigationMenuService,
    private sxPermissionService: SxPermissionService
  ) {
    this.appRouter = router;
  }

  ngOnInit() {
    if (this.node) {
      this.setClasses();
    }

    
  }

  ngOnChanges() {
    this.children = this.node && this.node.children ? this.node.children.filter(n => !n.hidden) : [];
    if (!!this.selectedNode) {
     let isSelectedNode = this.node.id === this.selectedNode.id || this.node.id === this.selectedNode.parentId;
     this.setExpanded(isSelectedNode);
    }

     if (this.meteringPoints && this.meteringPoints.loaded && this.meteringPoints.meteringPoints && this.meteringPoints.meteringPoints.length > 0 ) {
        // Ändrad så att om ALLA kunder är white label så sätts isWhiteLabel till true
        this.onlyExternalMeteringPoints = this.meteringPoints.meteringPoints.every((mp) => mp.id.toString().startsWith('20000'));
      }
  }

  get isSelected(): boolean{
    if (!(!!this.selectedNode)) return false;

    if (this.node.id === this.selectedNode.id)
      return true;

    return false;
  }

  setExpanded(isInSelectedHierarchy: boolean): void {
    
    if (isInSelectedHierarchy && this.hasItems()) {
      this.node.expanded = true;
    } else {
      if (this.collapseOnSelect) {
        this.node.expanded = false;
      }
    }
    this.setClasses();
  }

  useIndexOf(node: MenuItem) {
    return (!node.children || node.children.length === 0) && node.parentId === '-1' ? true : false;
  }

  getListStyle(): ListStyle {
    let leftpadding = this.level * 8;
    let rigthpadding = 0;
    if (this.level === 0) {
      leftpadding = this.node.externalRedirect ? leftpadding + 20 : leftpadding + 8; // No icon on leftside for external links
      rigthpadding = 48;
    } else {
      leftpadding = leftpadding + 16;
    }

    const styles = {
      background: 'null',
      padding: `0 ${rigthpadding}px 0 ${leftpadding}px`
    };

    return styles;
  }
  getListIcon(node: MenuItem): string {
    if (node.iconName !== null && node.iconName !== undefined && node.iconName !== '') {
      return `icon`;
    } else {
      return ``;
    }
  }
  getSelectedIcon() {
    if (this.isSelected && this.node.activeIconName) {
      return this.node.activeIconName;
    }
    return this.node.iconName;
  }
  getHrefTargetType() {
    if (this.node.hrefTargetType) {
      return this.node.hrefTargetType;
    }
    return MULTI_LEVEL_MENU_CONSTANT.DEFAULT_HREF_TARGET_TYPE;
  }

  hasItems(): boolean {
    if (this.children.length > 0) {
      let result = this.children.filter(x =>
        this.sxPermissionService.securityTrim(x.userTypes, x.permissions, null, null)
      );
      return result.length > 0 ? true : false;
    }
    return false;
  }
  hasIcon(): boolean {
    return this.getListIcon(this.node) !== '' ? true : false;
  }

  setClasses(): void {
    this.classes = {
      [`level-${this.level + 1}`]: true,
      'sx-multilevel-sidenav__submenu': this.hasItems()
    };
  }

  expand(): void {
    if (this.node.disabled) {
      return;
    }
    this.node.expanded = !this.node.expanded;

    this.firstInitializer = true;
    this.setClasses();
    event.stopImmediatePropagation();
  }

  select(): void {
    if (this.node.disabled) {
      return;
    }
    this.firstInitializer = true;
    this.setClasses();
    if (!!this.node.linkUrl && !this.node.externalRedirect) {
      this.router.navigate([this.node.linkUrl], { state: { goBackUrl: this.node.goBackUrl } });

      if (this.selectedNode && this.selectedNode.id === this.node.id){
        this.selectedListItem(this.node);
      }
    } else if (this.node.onSelected && typeof this.node.onSelected === 'function') {
      this.node.onSelected(this.node);
      this.selectedListItem(this.node);
    } else if (this.node.children === undefined || this.collapseOnSelect) {
      this.selectedListItem(this.node);
    }
  }

  selectedListItem(node: MenuItem): void {
    this.selectedItem.emit(node);
  }
}
